import { useState } from "react";
import { toast } from "sonner";
import { X as XIcon, Loader2, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Correct a container number typo (Sedem's flow).
 *
 * Two-step: preview (dry-run) → apply. Shows per-collection row counts so the
 * user sees exactly what will get renamed before committing.
 *
 * Props:
 *   oldContainerNumber — string
 *   onClose            — () => void
 *   onDone             — (newNumber) => void   parent reloads / navigates
 */
export default function CorrectContainerNumberDialog({ oldContainerNumber, onClose, onDone }) {
  const [newCn, setNewCn] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const runPreview = async () => {
    const clean = (newCn || "").toUpperCase().trim();
    if (!clean) return toast.error("Enter the corrected container number");
    if (clean === oldContainerNumber) return toast.error("New number must be different");
    setBusy(true);
    setPreview(null);
    try {
      const r = await api.post("/gate-events/correct-container-number", {
        old_container_number: oldContainerNumber,
        new_container_number: clean,
        apply: false,
      });
      setPreview(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Preview failed";
      toast.error(typeof msg === "string" ? msg : "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const r = await api.post("/gate-events/correct-container-number", {
        old_container_number: preview.old_cn,
        new_container_number: preview.new_cn,
        apply: true,
      });
      toast.success(`Renamed. ${Object.values(r.data.updated || {}).reduce((a, b) => a + b, 0)} rows updated.`);
      onDone && onDone(preview.new_cn);
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Rename failed";
      toast.error(typeof msg === "string" ? msg : "Rename failed");
    } finally {
      setBusy(false);
    }
  };

  const totalMatched = preview
    ? Object.values(preview.matched || {}).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="correct-cn-dialog"
    >
      <div className="bg-white border border-gray-300 max-w-lg w-full shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="font-display text-xl font-bold">Correct container number</h2>
            <div className="text-xs text-gray-500 font-mono mt-0.5">Current: {oldContainerNumber}</div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" data-testid="correct-cn-close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded-sm flex gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              This renames the container across <b>every</b> module — gate events, wash orders, EIRs, EDI messages, yard services, rentals, repairs, invoices.
              Any EDI already sent will be flagged for re-send so you can push a corrected message.
            </span>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
              Corrected container number *
            </label>
            <input
              value={newCn}
              onChange={(e) => { setNewCn(e.target.value.toUpperCase()); setPreview(null); }}
              placeholder="e.g. ARKU8553106"
              className="ps-input w-full font-mono"
              data-testid="correct-cn-new"
              autoFocus
            />
            <div className="text-[10px] text-gray-500 mt-1">Uppercase alphanumeric, 4–20 characters.</div>
          </div>

          {preview && (
            <div className="border border-gray-200 rounded-sm" data-testid="correct-cn-preview">
              <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-[0.16em] font-bold text-gray-600">
                Preview — {totalMatched} row(s) will be renamed
              </div>
              <div className="p-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
                {Object.entries(preview.matched || {})
                  .filter(([, n]) => n > 0)
                  .map(([coll, n]) => (
                    <div key={coll} className="flex justify-between">
                      <span className="text-gray-600">{coll}</span>
                      <span className="font-semibold">{n}</span>
                    </div>
                  ))}
                {totalMatched === 0 && (
                  <div className="col-span-2 text-gray-400 italic">
                    Nothing matches {preview.old_cn} — refusing rename.
                  </div>
                )}
              </div>
              {preview.edi_resend_would_flag > 0 && (
                <div className="px-3 py-2 border-t border-gray-200 bg-amber-50 text-[11px] text-amber-900">
                  {preview.edi_resend_would_flag} EDI message(s) already fired — they will be flagged for re-send.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="ps-btn ps-btn-secondary" data-testid="correct-cn-cancel">Cancel</button>
          {!preview ? (
            <button onClick={runPreview} disabled={busy || !newCn} className="ps-btn ps-btn-primary" data-testid="correct-cn-preview-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview"}
            </button>
          ) : (
            <button
              onClick={commit}
              disabled={busy || totalMatched === 0}
              className="ps-btn ps-btn-primary"
              data-testid="correct-cn-apply-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Rename ${totalMatched} row(s)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
